/**
 * **Which of a watcher's weapons can reach a target for Overwatch, and from how far.** One read
 * shared by the move-checkpoint offer (`checkpointRegistry.ts`'s `compileOverwatchOffers`: its
 * eligibility test and its prompt text) and the browser's take flow (preselecting and greying the
 * weapon cards), so the prompt, the chooser and the offer's own eligibility can't disagree
 * (`projects/overwatch-mid-move.md` 3.1-3.2).
 *
 * Its own module, like `moveReactionDecline.ts`, so the registry carries only the call and this
 * imports nothing from it.
 */
import { isItemDestroyed, superheavyBarsSkirmish, threatValue, weaponInstanceKey, worldFor, type Cell, type Combatant, type EncounterState } from "@lancer-vtt/domain";
import { footprintCells, footprintDistance } from "./effects/spatialAnswerer";

/** One weapon a watcher could fire as Overwatch, with its THREAT. */
export type OverwatchWeaponThreat = {
  /** The weapon's instance key (`weaponInstanceKey`, what a `request_attack` names); `undefined` for the unarmed strike. */
  weaponRef: string | undefined;
  name: string;
  threat: number;
};

/** The distance from a watcher to its target, and the watcher's weapons whose THREAT covers it. */
export type OverwatchReach = {
  distance: number;
  /** Loadout order. Empty when the target is out of every weapon's THREAT. */
  weapons: OverwatchWeaponThreat[];
};

/** The name the unarmed strike is shown under — `resolve/weapons.ts`'s `FALLBACK_WEAPON.name`. */
const UNARMED_STRIKE_NAME = "Unarmed strike";

/**
 * Every weapon `watcher` could fire as Overwatch, with its THREAT: live weapons that are neither
 * Ordnance nor Superheavy, or the unarmed strike when the loadout is empty.
 *
 * **Ordnance is excluded outright** (RAW p.105: "cannot be used for OVERWATCH"), mirroring
 * `checkOverwatchRange`'s flat prohibition. **Superheavy is excluded too** (Core p.70: "can only be
 * fired as part of a BARRAGE"), mirroring the take path's `checkSuperheavySkirmish` with verb
 * `"overwatch"`, which refuses the shot (the Leviathan's Standard profile, which may Skirmish, is
 * the one Superheavy kept — Core p.134); offering it would halt the move on a prompt nobody can
 * act on (the Demolisher's hammer). **No fallback to an unarmed strike when the watcher is
 * actually armed**: a watcher whose only live weapons are Ordnance or Superheavy gets `[]` (no threat at all),
 * not bare fists; `selectWeapon` never falls back to `FALLBACK_WEAPON` while real loadout weapons
 * exist either. A genuinely weaponless watcher (empty loadout) threatens with the unarmed strike
 * (`threatValue([])` === 1, the value `FALLBACK_WEAPON`'s empty `range` resolves to). Destroyed
 * weapons are excluded ({@link isItemDestroyed}, parity with `selectWeapon`'s own filter).
 *
 * **The empty-loadout branch is NOT the place to exclude an object/deployable/drone.** Who may take
 * a reaction is decided on `kind` by `compileOverwatchOffers`'s gate (f) before this runs. A
 * weaponless mech or pilot legitimately threatens 1.
 *
 * Loading, Recharge and Limited are not read here: they gate whether a chosen weapon fires
 * (`checkLoaded` &c. on the `request_attack`), not whether the watcher threatens the square.
 */
export function overwatchWeaponThreats(watcher: Combatant): OverwatchWeaponThreat[] {
  const loadout = watcher.base.weapons ?? [];
  if (loadout.length === 0) return [{ weaponRef: undefined, name: UNARMED_STRIKE_NAME, threat: threatValue([]) }];
  return loadout
    .filter((w) => !w.ordnance && !superheavyBarsSkirmish(w) && !isItemDestroyed(watcher.current.resources, weaponInstanceKey(w)))
    .map((w) => ({ weaponRef: weaponInstanceKey(w), name: w.name, threat: threatValue(w.range ?? []) }));
}

/**
 * `watcher`'s Overwatch reach against a target footprint: the footprint-to-footprint distance, and
 * the {@link overwatchWeaponThreats} whose THREAT is at least that. `undefined` when the distance
 * can't be measured (the watcher is unplaced, or `targetCells` is empty).
 *
 * The checkpoint passes the mover's footprint at its declared `from`; {@link overwatchReachOf}
 * measures a placed target where it stands.
 */
export function overwatchReachAt(state: EncounterState, args: { watcher: Combatant; targetCells: Cell[] }): OverwatchReach | undefined {
  const world = worldFor(state);
  const distance = footprintDistance(world, footprintCells(world, args.watcher), args.targetCells);
  if (distance === null) return undefined;
  return { distance, weapons: overwatchWeaponThreats(args.watcher).filter((w) => w.threat >= distance) };
}

/** {@link overwatchReachAt} against a placed target where it stands now (the take flow: the mover is still at `from`). */
export function overwatchReachOf(state: EncounterState, args: { watcherId: string; targetId: string }): OverwatchReach | undefined {
  const watcher = state.combatants[args.watcherId];
  if (!watcher) return undefined;
  return overwatchReachAt(state, { watcher, targetCells: footprintCells(worldFor(state), state.combatants[args.targetId]) });
}

/** `(2 away; Carbon Fiber Sword, Threat 2)` — the prompt's parenthetical. Several weapons join with `; `. */
export function formatOverwatchReach(reach: OverwatchReach): string {
  const weapons = reach.weapons.map((w) => `${w.name}, Threat ${w.threat}`);
  return `(${[`${reach.distance} away`, ...weapons].join("; ")})`;
}
